import React, { useEffect } from 'react';
import dayjs from 'dayjs';
import isBetweenPlugin from 'dayjs/plugin/isBetween';
import CustomStaticPeriodDatePicker from '../../../../../Common/CustomStaticPeriodDatePicker';
import { useDispatch, useSelector } from 'react-redux';
import * as yup from 'yup';
import styles from './ClosuresModal.module.scss';
import { Field, Form, Formik } from 'formik';
import { getWorker, getWorkers } from '../../../../store/selectors';
import { Button } from '@mui/material';
import { salonManagementActions } from '../../../../store';
import SelectorMenu from '../../../../../Common/CustomSelector/SelectorMenu';
import clx from 'classnames';
import { schedulerActions } from '../../../../../Scheduler/store';
import useWindowSize from '../../../../../../utils/useWindowSize';
import TextField from '../../../../../Common/TextField';
import { useTranslation } from 'react-i18next';

dayjs.extend(isBetweenPlugin);

const normalizeWorkerId = (id) => {
  if (id == null || id === '') return null;
  const n = Number(id);
  return Number.isFinite(n) ? n : id;
};

const sameWorkerId = (a, b) => String(a) === String(b);

const findBlockedDates = (workers, activeWorkers = []) => {
  const filteredWorkers = workers.filter((worker) =>
    activeWorkers.some((id) => sameWorkerId(id, worker.id)),
  );
  const closuresDates = filteredWorkers.reduce((acc, worker) => {
    const workerClosuresDates =
      worker.closures?.map(({ data_chiusura }) => dayjs(data_chiusura).format('YYYY-MM-DD')) ||
      [];
    return [...acc, ...workerClosuresDates];
  }, []);

  const extraWorkingDates = filteredWorkers.reduce((acc, worker) => {
    const workerExtraWorkingDates =
      worker.extraWorkingTime?.reduce((inner, { dataInizio, dataFine, giorno }) => {
        const dif = dataInizio && dataFine && dayjs(dataFine).diff(dataInizio, 'day');
        if (dif >= 0) {
          const days =
            Array.from({ length: dif + 1 }, (_, i) =>
              dayjs(dataInizio).add(i, 'days').format('YYYY-MM-DD'),
            ).filter((day) => dayjs(day).day() === +giorno) || [];
          return [...inner, ...days];
        }
        return inner;
      }, []) || [];
    return [...acc, ...workerExtraWorkingDates];
  }, []);

  return [...new Set([...closuresDates, ...extraWorkingDates])];
};

/** Poltrone già chiuse quel giorno → selezionate con − (come aperture straordinarie). */
const findWorkersWithClosure = (workers, startDay) => {
  if (!startDay) return [];
  return workers.reduce((acc, worker) => {
    const closure = worker.closures?.find(({ data_chiusura }) =>
      dayjs(data_chiusura).isSame(startDay, 'day'),
    );
    if (!closure) return acc;
    const id = normalizeWorkerId(closure.poltrona ?? worker.id ?? worker.idpoltrona);
    return id != null ? [...acc, id] : acc;
  }, []);
};

const datesBetween = (start, end) => {
  if (!start || !end) return [];
  const out = [];
  let cursor = dayjs(start).startOf('day');
  const last = dayjs(end).startOf('day');
  while (cursor.isBefore(last) || cursor.isSame(last, 'day')) {
    out.push(cursor.format('YYYY-MM-DD'));
    cursor = cursor.add(1, 'day');
  }
  return out;
};

const MOTIVATIONS = [
  'workers.closures.motivations.holiday',
  'workers.closures.motivations.vacation',
  'workers.closures.motivations.noServices',
  'workers.closures.motivations.personal',
];

export default ({ closeModal, isOneDay, dayValue }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const worker = useSelector(getWorker());
  const workers = useSelector(getWorkers());
  const { isMobile } = useWindowSize();

  const formedWorkers = workers.map((item) => ({
    name: item.name,
    value: normalizeWorkerId(item.id),
    icon: item.icon,
  }));

  useEffect(() => {
    dispatch(salonManagementActions.GET_WORKERS.REQUEST({ query: { workTime: true } }));
  }, [dispatch]);

  const handleClosureSuccess = () => {
    closeModal();
    // Giorno singolo: update ottimistico sul calendario (SET/REMOVE_CALENDAR_WORKER_CLOSURES).
    // Un refetch immediato di getCalendario spesso torna senza la chiusura e cancella il rosso.
    if (isOneDay) return;
    window.location.reload();
  };

  const existingClosureIds = isOneDay
    ? findWorkersWithClosure(workers, dayValue.startDay)
    : [];

  const initialActiveWorkers = [
    ...new Set(
      [
        normalizeWorkerId(worker?.idpoltrona),
        ...existingClosureIds,
      ].filter((id) => id != null),
    ),
  ];

  const initialValues = {
    datePeriod: {
      start: isOneDay ? dayjs(dayValue.startDay) : null,
      end: isOneDay ? dayjs(dayValue.startDay) : null,
      blockedDates: isOneDay ? [] : findBlockedDates(workers, [worker?.idpoltrona]),
    },
    motivation: '',
    activeWorkersIds: initialActiveWorkers,
    /** Snapshot apertura modal: chi aveva già chiusura (per capire cosa togliere al Salva). */
    existingClosureWorkerIds: existingClosureIds,
  };

  const validationSchema = {
    datePeriod: yup
      .object()
      .shape({
        start: yup.date().typeError(t('workers.closuresModal.validation.selectDate')).required(),
        end: yup.date().typeError(t('workers.closuresModal.validation.selectDate')).required(),
      })
      .test('checkDate', t('workers.closuresModal.validation.dateAlreadyTaken'), (val) => {
        const { start, end, blockedDates } = val;
        const matchedDate = (blockedDates || []).find(
          (closureDate) =>
            start && end && dayjs(closureDate).isBetween(start, end, null, '[]'),
        );
        return !matchedDate;
      }),
  };

  return (
    <div
      className={clx(styles.worker_closures_container, {
        [styles.worker_closures_container_one_day]: isOneDay,
        [styles.worker_closures_container_mobile]: isMobile,
      })}
    >
      <Formik
        enableReinitialize
        initialValues={initialValues}
        validationSchema={yup.object().shape(validationSchema)}
        onSubmit={(values) => {
          const selected = (values.activeWorkersIds || []).map(normalizeWorkerId);
          const existing = (values.existingClosureWorkerIds || []).map(normalizeWorkerId);
          const toAdd = selected.filter(
            (id) => id != null && !existing.some((e) => sameWorkerId(e, id)),
          );
          const toRemove = existing.filter(
            (id) => id != null && !selected.some((s) => sameWorkerId(s, id)),
          );

          const dates = datesBetween(values.datePeriod?.start, values.datePeriod?.end);
          const deleteOps = toRemove.length * Math.max(dates.length, 1);
          const addOps = toAdd.length > 0 ? 1 : 0;
          let pending = deleteOps + addOps;

          if (pending === 0) {
            handleClosureSuccess();
            return;
          }

          const onStepDone = () => {
            pending -= 1;
            if (pending <= 0) handleClosureSuccess();
          };

          toRemove.forEach((workerId) => {
            dates.forEach((date) => {
              if (isOneDay) {
                dispatch(
                  schedulerActions.REMOVE_CALENDAR_WORKER_CLOSURES.SUCCESS({
                    activeWorkersIds: [workerId],
                    dataChiusura: date,
                  }),
                );
              }
              dispatch(
                salonManagementActions.DELETE_WORKER_CLOSURES.REQUEST(
                  { idPoltrona: workerId, data: date },
                  onStepDone,
                ),
              );
            });
          });

          if (toAdd.length > 0) {
            const req = {
              idPoltrona: worker.idpoltrona,
              activeWorkersIds: toAdd,
              dataChiusura: values.datePeriod?.start.format('YYYY-MM-DD'),
              dataChiusuraFine: values.datePeriod?.end.format('YYYY-MM-DD'),
              tipo_chiusura: MOTIVATIONS.findIndex((i) => i === values?.motivation) + 1,
            };
            if (isOneDay) {
              dispatch(
                schedulerActions.SET_CALENDAR_WORKER_CLOSURES.REQUEST(req, onStepDone),
              );
            } else {
              dispatch(salonManagementActions.SET_WORKER_CLOSURES.REQUEST(req, onStepDone));
            }
          }
        }}
      >
        {({ values, setFieldValue, errors, touched }) => (
          <Form className={styles.closures_modal_wrapper}>
            <div className={styles.body}>
              <div className={styles.reverse_column}>
                <div className={styles.block}>
                  <div className={styles.range_picker_container}>
                    <div className={styles.date_selector_title}>
                      {t('workers.closuresModal.selectPeriod')}
                    </div>
                    <div style={{ width: 'fit-content' }}>
                      <CustomStaticPeriodDatePicker
                        period={values.datePeriod}
                        setPeriod={(period) =>
                          setFieldValue('datePeriod', { ...values.datePeriod, ...period })
                        }
                        error={
                          errors.datePeriod?.start || errors.datePeriod?.end || errors.datePeriod
                        }
                        touched={touched.datePeriod}
                        disabledDates={values.datePeriod.blockedDates}
                      />
                    </div>
                  </div>
                  <div className={styles.motivation__menu}>
                    <div className={styles.motivation__menu_title}>
                      {t('workers.closuresModal.motivation')}{' '}
                      <span>{t('workers.closuresModal.optional')}</span>
                    </div>
                    <SelectorMenu
                      isMultipleOne
                      fullHeight
                      setIsDropdownOpen={() => {}}
                      options={MOTIVATIONS.map((i) => ({ name: t(i), value: i }))}
                      setValue={(value) => {
                        setFieldValue('motivation', value);
                      }}
                      value={values.motivation}
                      error={touched.motivation && errors.motivation}
                    />
                  </div>
                </div>

                <div className={styles.workers__menu}>
                  <div className={styles.workers__menu_title}>
                    {t('workers.closuresModal.chairs')}
                  </div>
                  <SelectorMenu
                    isMultiple
                    fullHeight
                    setIsDropdownOpen={() => {}}
                    options={formedWorkers}
                    setValue={(value) => {
                      const next = (value || []).map(normalizeWorkerId).filter((id) => id != null);
                      setFieldValue('activeWorkersIds', next);
                      setFieldValue('datePeriod.blockedDates', findBlockedDates(workers, next));
                    }}
                    value={values.activeWorkersIds}
                    error={touched.activeWorkersIds && errors.activeWorkersIds}
                  />
                </div>
              </div>

              <div className={styles.comment}>
                <div className={styles.comment__title}>
                  {t('workers.closuresModal.comment')}
                  <span> {t('workers.closuresModal.commentOptional')}</span>
                </div>
                <Field
                  name="note"
                  id="note"
                  textarea
                  placeHolder={t('workers.closuresModal.placeholder')}
                  height="100px"
                  className={styles.input}
                  component={TextField}
                />
              </div>
            </div>
            <hr className={styles.line} />
            <div className={styles.footer}>
              <div className={styles.actions_container}>
                <Button className={styles.confirm_button} variant="contained" type="submit">
                  {t('common.actions.save')}
                </Button>
              </div>
            </div>
          </Form>
        )}
      </Formik>
    </div>
  );
};
